/**
 * Admin → Legal → Versión (detalle)
 * Carga exactamente document_version_id (params.versionId), no "current published".
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LegalMarkdown } from "@/components/legal/LegalMarkdown";
import {
  LEGAL_TYPE_LABELS,
  LegalBackLink,
  LegalVersionActions,
  VersionStatusBadge,
  CopyHashButton,
} from "@/components/legal/legal-admin-shared";

export const Route = createFileRoute("/admin/ops/legal/documents/$docId/versions/$versionId")({
  component: LegalVersionDetailPage,
  head: () => ({ meta: [{ title: "Versión legal — Admin" }] }),
});

interface VersionDetail {
  id: string;
  documentId: string;
  version: string;
  status: string;
  content: string;
  contentHash: string;
  summary: string | null;
  changeSummary: string | null;
  requiresReacceptance: boolean;
  createdAt: string;
  approvedAt: string | null;
  publishedAt: string | null;
  createdBy: string | null;
  approvedBy: string | null;
  publishedBy: string | null;
}

interface DocMeta {
  id: string;
  type: string;
  title: string;
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[10rem_1fr] gap-2 py-1.5 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-all font-medium text-foreground">{value}</dd>
    </div>
  );
}

function LegalVersionDetailPage() {
  const { docId, versionId } = Route.useParams();
  const [doc, setDoc] = useState<DocMeta | null>(null);
  const [version, setVersion] = useState<VersionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    setLoading(true);
    fetch(`/api/admin/legal/${docId}/versions/${versionId}`)
      .then(async (r) => {
        if (!r.ok) throw new Error("Versión no encontrada");
        return r.json() as Promise<{ document: DocMeta; version: VersionDetail }>;
      })
      .then((d) => {
        setDoc(d.document);
        setVersion(d.version);
        setError(null);
        setLoading(false);
      })
      .catch(() => {
        setError("No se pudo cargar esta versión");
        setLoading(false);
      });
  }, [docId, versionId]);

  useEffect(reload, [reload]);

  return (
    <div className="p-6">
      <LegalBackLink />

      {loading && <p className="text-sm text-muted-foreground">Cargando…</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}

      {version && doc && (
        <>
          <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground">
                {doc.title}
                {LEGAL_TYPE_LABELS[doc.type] ? ` · ${LEGAL_TYPE_LABELS[doc.type]}` : ""}
              </p>
              <h1 className="text-2xl font-bold text-foreground" data-testid="legal-version-heading">
                Version {version.version}
              </h1>
              <div className="mt-2">
                <VersionStatusBadge status={version.status} />
              </div>
            </div>
            <LegalVersionActions docId={docId} version={version} onDone={reload} />
          </div>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-base">Metadata</CardTitle>
            </CardHeader>
            <CardContent>
              <dl>
                <MetaRow label="Documento" value={doc.title} />
                <MetaRow label="Type" value={doc.type} />
                <MetaRow label="Versión" value={version.version} />
                <MetaRow label="document_version_id" value={version.id} />
                <MetaRow label="Estado" value={version.status.toUpperCase()} />
                <MetaRow label="Created at" value={new Date(version.createdAt).toLocaleString("es-AR")} />
                <MetaRow label="Approved at" value={version.approvedAt ? new Date(version.approvedAt).toLocaleString("es-AR") : "—"} />
                <MetaRow label="Published at" value={version.publishedAt ? new Date(version.publishedAt).toLocaleString("es-AR") : "—"} />
                <MetaRow label="Archived at" value={version.status === "archived" ? "(sin columna archived_at en schema)" : "—"} />
                <MetaRow label="Created by" value={version.createdBy ?? "—"} />
                <MetaRow label="Approved by" value={version.approvedBy ?? "—"} />
                <MetaRow label="Published by" value={version.publishedBy ?? "—"} />
                <MetaRow label="Change summary" value={version.changeSummary ?? version.summary ?? "—"} />
                <MetaRow label="Requires reacceptance" value={version.requiresReacceptance ? "sí" : "no"} />
              </dl>
              <div className="mt-3 border-t border-border pt-3">
                <p className="mb-1.5 text-sm text-muted-foreground">Content hash</p>
                <CopyHashButton hash={version.contentHash} />
                <p className="mt-1 font-mono text-[11px] break-all text-muted-foreground">{version.contentHash}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Contenido</CardTitle>
            </CardHeader>
            <CardContent>
              <div data-testid="legal-version-content">
                <LegalMarkdown content={version.content} />
              </div>
              <p className="mt-6 text-xs text-muted-foreground">
                Este contenido corresponde exactamente a <code>sm_legal_document_versions.content</code> de
                la versión <code>{version.id}</code>. Es inmutable una vez creado.
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
